import type { UserWorkspace, WorkspaceUser } from '@/api/types'

/**
 * Cache updaters for the workspace list.
 *
 * Pure and immutable: each returns a new array, or the array it was given when
 * nothing matched, so React sees a new reference exactly when something changed.
 *
 * Ported from the `force*` actions in
 * `web-frontend/modules/core/store/workspace.js`. Vuex mutated in place and
 * could therefore be careless about a workspace that is not in the list; here
 * every updater is written to be a no-op in that case, because a realtime event
 * routinely arrives for a workspace this client has never loaded.
 */

export function addWorkspace(
  workspaces: UserWorkspace[],
  workspace: UserWorkspace,
): UserWorkspace[] {
  const index = workspaces.findIndex((existing) => existing.id === workspace.id)
  if (index === -1) return [...workspaces, workspace]

  // A restore of a workspace still in the list, or a create racing a fetch.
  // Replacing rather than appending keeps it from appearing twice.
  const next = [...workspaces]
  next[index] = workspace
  return next
}

export function updateWorkspace(
  workspaces: UserWorkspace[],
  workspaceId: number,
  values: Partial<UserWorkspace>,
): UserWorkspace[] {
  const index = workspaces.findIndex((workspace) => workspace.id === workspaceId)
  if (index === -1) return workspaces

  const next = [...workspaces]
  next[index] = { ...workspaces[index]!, ...values }
  return next
}

export function removeWorkspace(
  workspaces: UserWorkspace[],
  workspaceId: number,
): UserWorkspace[] {
  const next = workspaces.filter((workspace) => workspace.id !== workspaceId)
  return next.length === workspaces.length ? workspaces : next
}

export function addWorkspaceUser(
  workspaces: UserWorkspace[],
  workspaceId: number,
  user: WorkspaceUser,
): UserWorkspace[] {
  return updateWorkspaceById(workspaces, workspaceId, (workspace) => ({
    ...workspace,
    users: [...workspace.users, user],
  }))
}

export function updateWorkspaceUser(
  workspaces: UserWorkspace[],
  workspaceId: number,
  workspaceUserId: number,
  values: Partial<WorkspaceUser>,
): UserWorkspace[] {
  return updateWorkspaceById(workspaces, workspaceId, (workspace) => {
    const index = workspace.users.findIndex((user) => user.id === workspaceUserId)
    if (index === -1) return workspace

    const users = [...workspace.users]
    users[index] = { ...workspace.users[index]!, ...values }
    return { ...workspace, users }
  })
}

export function removeWorkspaceUser(
  workspaces: UserWorkspace[],
  workspaceId: number,
  workspaceUserId: number,
): UserWorkspace[] {
  return updateWorkspaceById(workspaces, workspaceId, (workspace) => {
    const users = workspace.users.filter((user) => user.id !== workspaceUserId)
    return users.length === workspace.users.length ? workspace : { ...workspace, users }
  })
}

/**
 * Applies `values` to one person's membership of every workspace they are in.
 *
 * A rename is a fact about the account, and the same person is a separate
 * `WorkspaceUser` row in each workspace. Patching only the workspace on screen
 * leaves their old name in every other one until a refetch.
 */
export function updateUserEverywhere(
  workspaces: UserWorkspace[],
  userId: number,
  values: Partial<WorkspaceUser>,
): UserWorkspace[] {
  let changed = false
  const next = workspaces.map((workspace) => {
    const index = workspace.users.findIndex((user) => user.user_id === userId)
    if (index === -1) return workspace

    changed = true
    const users = [...workspace.users]
    users[index] = { ...workspace.users[index]!, ...values }
    return { ...workspace, users }
  })
  return changed ? next : workspaces
}

function updateWorkspaceById(
  workspaces: UserWorkspace[],
  workspaceId: number,
  update: (workspace: UserWorkspace) => UserWorkspace,
): UserWorkspace[] {
  const index = workspaces.findIndex((workspace) => workspace.id === workspaceId)
  if (index === -1) return workspaces

  const updated = update(workspaces[index]!)
  if (updated === workspaces[index]) return workspaces

  const next = [...workspaces]
  next[index] = updated
  return next
}
